'use client'

import { useState } from 'react'
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { HelpCircle, FileText, CircleDollarSign, Settings, Database, Search, Mail } from 'lucide-react'
import Link from 'next/link'

const faqData = [
  {
    category: "General",
    icon: HelpCircle,
    questions: [
      {
        question: 'What is GovCon Data?',
        answer: 'GovCon Data is an AI-powered platform designed to help businesses of all sizes find, analyze, and win government contracts. We consolidate federal, state, and local opportunities, providing comprehensive tools, AI insights, and analytics.',
      },
      {
        question: 'Who is GovCon Data for?',
        answer: 'Our platform is built for prime contractors, subcontractors, consultants, and any business seeking to grow through government contracting at the federal, state, or local level.',
      },
      {
        question: 'How is GovCon Data different from SAM.gov or other portals?',
        answer: 'While portals like SAM.gov are essential for registration and finding federal opportunities, GovCon Data offers a unified experience across multiple sources (federal, state, local), enhanced search capabilities, AI-driven analysis, market intelligence, proposal tools, and advanced analytics that go beyond basic listings.',
      },
    ]
  },
  {
    category: "Platform Features",
    icon: Settings,
    questions: [
      {
        question: 'What types of contracts can I find?',
        answer: 'Our platform aggregates a wide range of opportunities, including RFPs, RFQs, RFIs, BPAs, IDIQs, grants, and other solicitation types across numerous industries and government agencies at federal, state, and local levels.',
      },
      {
        question: 'How does the Contract Search Agent work?',
        answer: 'Our Contract Search Agent uses natural language processing and semantic search. You can describe your needs in plain English, and the AI will find the most relevant opportunities, saving you time scrolling through irrelevant listings.',
      },
      {
        question: 'What kind of analytics do you offer?',
        answer: 'We provide advanced analytics to help you understand market trends, benchmark your performance against competitors, analyze win/loss factors, and identify high-value opportunities using visualizations and AI-driven scoring.',
      },
      {
        question: 'Is there an AI Proposal Generator?',
        answer: 'Yes, our AI Proposal Generator assists in drafting compelling proposals by suggesting relevant content, ensuring compliance with solicitation requirements, analyzing win themes, and leveraging past performance data. Note: Some AI features may be in development or available on specific plans.',
      }
    ]
  },
  {
    category: "Billing & Subscription",
    icon: CircleDollarSign,
    questions: [
      {
        question: 'What are the pricing plans?',
        answer: 'We offer a single Professional plan at $20/month or $200/year (save $40). The Professional plan includes full dashboard access, AI-matched opportunities, bid management, the AI proposal generator, unlimited opportunity access, and 5M AI tokens per month. You can start with a 14-day free trial — no credit card required.',
      },
      {
        question: 'Is there a free trial?',
        answer: 'Yes! We offer a 14-day free trial of our Professional plan with no credit card required. You get full access to all features during the trial period. Visit our Pricing page to get started.',
      },
      {
        question: 'How can I cancel my subscription?',
        answer: 'You can manage your subscription, including cancellations, through your account settings dashboard. If you encounter any issues, please contact our support team.',
      },
    ]
  },
  {
    category: "Data & Security",
    icon: Database,
    questions: [
      {
        question: 'Where does the contract data come from?',
        answer: 'We aggregate data from numerous official sources, including SAM.gov, state procurement portals, local government websites, and other public databases. We strive to keep the information accurate and up-to-date.',
      },
      {
        question: 'How often is the data updated?',
        answer: 'Data is updated frequently throughout the day as new information becomes available from the source portals. Real-time alerts can notify you of new matching opportunities.',
      },
      {
        question: 'How is my data secured?',
        answer: 'We take data security very seriously. We employ industry-standard security measures, encryption protocols, and access controls to protect your account information and any proprietary data you may store on the platform.',
      },
    ]
  }
]

export default function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(faq => 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="bg-gradient-to-b from-background to-muted/30 min-h-screen py-16 lg:py-24">
      <div className="container max-w-5xl mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <HelpCircle className="h-16 w-16 text-primary mx-auto mb-6" />
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            Find answers to common questions about our platform, features, billing, and more.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-10 max-w-2xl mx-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input 
            type="search"
            placeholder="Search questions or keywords..."
            className="w-full pl-10 pr-4 py-3 text-base rounded-lg shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-8">
          {filteredData.length > 0 ? (
            filteredData.map((category) => (
              <div key={category.category}>
                <h2 className="text-2xl font-semibold mb-4 flex items-center gap-3">
                  <category.icon className="h-6 w-6 text-primary" />
                  {category.category}
                </h2>
                <Accordion type="single" collapsible className="w-full space-y-3">
                  {category.questions.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${category.category}-${index}`} className="bg-card border rounded-lg px-4 shadow-sm hover:shadow-md transition-shadow">
                      <AccordionTrigger className="text-left font-medium hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pt-2 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          ) : (
            <div className="text-center text-muted-foreground py-10">
              <p className="text-lg mb-4">No results found for "{searchTerm}".</p>
              <p>Try searching for different keywords or browse the categories.</p>
            </div>
          )}
        </div>

        {/* Contact Support Fallback */}
        <div className="mt-16 text-center border-t pt-10">
          <h3 className="text-xl font-semibold mb-3">Can't find the answer you're looking for?</h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Our support team is ready to help. Get in touch with us for further assistance.
          </p>
          <Button asChild size="lg">
            <Link href="/contact">
              <Mail className="mr-2 h-5 w-5" /> Contact Support
            </Link>
          </Button>
        </div>

      </div>
    </div>
  )
} 